import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { SectionHeading } from '@/components/ui/SectionHeading'

interface GalleryImage {
  src: string
  alt?: string
  caption?: string
}

interface TreatmentGalleryProps {
  title?: string
  subtitle?: string
  images: GalleryImage[]
  className?: string
}

export function TreatmentGallery({
  title,
  subtitle,
  images,
  className,
}: TreatmentGalleryProps) {
  if (!images || images.length === 0) return null

  return (
    <section className={cn('bg-white py-16 md:py-24', className)}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          title={title || ''}
          subtitle={subtitle}
          centered
        />

        <div
          className={cn(
            'grid gap-4 md:gap-6',
            title && 'mt-10 md:mt-12',
            images.length === 1
              ? 'grid-cols-1 max-w-2xl mx-auto'
              : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
          )}
        >
          {images.map((image, index) => (
            <motion.figure
              key={index}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.4, delay: index * 0.08 }}
              className="group"
            >
              <div className="aspect-[4/3] rounded-xl overflow-hidden bg-cream">
                <img
                  src={image.src}
                  alt={image.alt || ''}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              {image.caption && (
                <figcaption className="mt-2 text-center text-sm text-muted">
                  {image.caption}
                </figcaption>
              )}
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  )
}
